import CustomerVerifyEmail from "@components/dialogs/customer_verify_email"
import ResendVerificationEmail from "@components/dialogs/resend_verification_email"
import ResetPassword from "@components/dialogs/reset_password"
import UpdateCustomerUser from "@components/dialogs/update_customer_user"
import GridTable from "@components/grid-table"
import {
  CHECKBOX_BORDER_COLOR,
  GRAY_COLOR,
  WATSAPP_ACTIVE
} from "@constants/colors"
import { useCustomerPageContext, UsersData } from "@contexts/customer"
import { customersGetUsers } from "@domain/customer/infos"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import SearchIcon from "@mui/icons-material/Search"
import { Avatar, Box, IconButton, TextField, Typography } from "@mui/material"
import Stack from "@mui/material/Stack"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import PopperMenu from "@pages/customer/infos/tabs/users/popper_menu"
import { showErrorToast } from "@utils/toast"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { CustomerPerson, CustomerType } from "__generated__/graphql"
import styles from "./styles"
import DeleteCustomerUser from "@components/dialogs/delete_customer_user"

export default function CustomerUsersPage() {
  const { translate, setUsers, users, isLoading, pagination, setPagination } =
    useCustomerPageContext()
  const [optionAnchorEl, setOptionAnchorEl] =
    useState<HTMLButtonElement | null>(null)
  const [clickedRow, setClickedRow] = useState<CustomerPerson>()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isVerifyEmailOpen, setIsVerifyEmailOpen] = useState(false)

  const [totalUserCount, setTotalUserCount] = useState<number>(users.length)

  const [isResendVerificationOpen, setIsResendVerificationOpen] =
    useState(false)

  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false)

  const [searchText, setSearchText] = useState<string>("")
  const [debouncedValue, setDebouncedValue] = useState<string>("")

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(searchText)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchText])

  const handlePopClose = () => {
    setOptionAnchorEl(null)
  }

  const handleEditClose = () => {
    setIsEditOpen(false)
  }

  const handleDeleteClose = () => {
    setIsDeleteOpen(false)
  }

  const handleEditOpen = () => {
    setIsEditOpen(true)
  }

  const handleDeleteOpen = () => {
    setIsDeleteOpen(true)
  }

  const handleVerifyEmailClose = () => {
    setIsVerifyEmailOpen(false)
  }

  const handleVerifyEmailOpen = () => {
    setIsVerifyEmailOpen(true)
  }

  const handleResendVerifyEmailOpen = () => {
    setIsResendVerificationOpen(true)
  }

  const handleResendVerifyEmailClose = () => {
    setIsResendVerificationOpen(false)
  }

  const handleResetPasswordOpen = () => {
    setIsResetPasswordOpen(true)
  }

  const handleResetPasswordClose = () => {
    setIsResetPasswordOpen(false)
  }

  const handleOptionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: UsersData
  ) => {
    setOptionAnchorEl(event.currentTarget)
    const data = users.find((item) => item._id === row.id)
    setClickedRow(data)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const { id } = useParams()

  const fetchUserData = useCallback(async () => {
    if (!id) return false
    try {
      const { isSuccess, data } = await customersGetUsers({
        customerId: id,
        // pageSize: 15,
        pageSize: pagination?.pageSize || 15,
        page: (pagination?.page || 0) + 1,
        // page: 1,
        search: debouncedValue
      })
      if (isSuccess) {
        const newUsers = data?.docs
        setUsers?.(newUsers as CustomerPerson[])
        setTotalUserCount(data?.totalDocs || 0)
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      showErrorToast(String(error))
    }
  }, [id, pagination, debouncedValue])

  useEffect(() => {
    fetchUserData()
  }, [pagination, debouncedValue])

  const [rows, setRows] = useState<UsersData[]>([])

  useEffect(() => {
    if (users) {
      const data = users?.map((doc) => {
        return {
          id: doc._id,
          role: doc.type,
          phone: doc.phone,
          email: doc.email,
          status: doc.isEmailVerified
            ? translate("verified")
            : translate("wating_email_verification"),
          user: `${doc?.firstName} ${doc?.lastName}`,
          profilePhoto: doc.profilePhoto,
          isArchived: doc.archivedAt ? true : false
        }
      })

      setRows(data as unknown as UsersData[])
    }
  }, [users])

  // const apiRef = useGridApiRef()

  const fieldWidth = 100

  const columns: GridColDef[] = [
    {
      field: "user",
      minWidth: 220,
      headerName: translate("user"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("user")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Stack flexDirection={"row"} gap={1}>
          <Avatar src={params.row.profilePhoto} sx={{ mt: 1 }} />
          <Stack>
            <Typography>{params.value}</Typography>
            <Typography>{params.row.email}</Typography>
          </Stack>
        </Stack>
      ),
      sortable: true,
      disableColumnMenu: true,
      flex: 1
    },
    {
      field: "role",
      minWidth: fieldWidth,
      headerName: translate("role"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("role")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color:
              params.value == "OWNER" || params.value == "ROBOT"
                ? WATSAPP_ACTIVE
                : params.value == "ADMIN"
                  ? "#157CFC"
                  : params.value == "PERSONEL"
                    ? "orange"
                    : GRAY_COLOR
          }}
        >
          {params.value
            ? params.value.charAt(0) + params.value.substring(1).toLowerCase()
            : ""}
        </Box>
      ),
      disableColumnMenu: true,
      flex: 1
    },
    {
      field: "phone",
      minWidth: 150,
      headerName: translate("phone"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("phone")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.value
            ? params.value.charAt(0) + params.value.substring(1).toLowerCase()
            : "-"}
        </Box>
      ),
      disableColumnMenu: true,
      flex: 1
    },
    {
      field: "status",
      minWidth: 150,
      headerName: translate("verification_status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("verification_status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color:
              params.value === translate("verified")
                ? WATSAPP_ACTIVE
                : GRAY_COLOR
          }}
        >
          {params.value}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true,
      flex: 1
    },
    {
      field: "isArchived",
      minWidth: 150,
      headerName: translate("status"),
      renderHeader: () => (
        <Box sx={styles.headerItems}>{translate("status")}</Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: params.value ? "#D32F2F" : WATSAPP_ACTIVE
          }}
        >
          {params.value ? "Archived" : "Active"}
        </Box>
      ),
      sortable: true,
      disableColumnMenu: true,
      flex: 1
    },
    {
      field: "actions",
      minWidth: 100,
      headerName: "Actions",
      renderHeader: () => <Box></Box>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as UsersData
        const options = [
          {
            headerName: translate("edit"),
            color: "#000",
            action: handleEditOpen
          },
          {
            headerName: translate("delete"),
            color: "#000",
            action: handleDeleteOpen
          }
        ]

        // verify email option
        if (
          !clickedRow?.isEmailVerified &&
          clickedRow?.type !== CustomerType.Robot
        ) {
          options.push({
            headerName: translate("verify"),
            color: "#000",
            action: handleVerifyEmailOpen
          })
        }

        // resend send email verification option
        if (
          !clickedRow?.isEmailVerified &&
          clickedRow?.type !== CustomerType.Robot
        ) {
          options.push({
            headerName: translate("resend_email_verification"),
            color: "#000",
            action: handleResendVerifyEmailOpen
          })
        }

        // reset password
        if (clickedRow?.type !== CustomerType.Robot) {
          options.push({
            headerName: translate("reset_password"),
            color: "#000",
            action: handleResetPasswordOpen
          })
        }

        return (
          <Box>
            <IconButton onClick={(event) => handleOptionClick(event, row)}>
              <MoreVertIcon />
            </IconButton>
            <PopperMenu
              open={Boolean(optionAnchorEl)}
              options={options}
              anchorEl={optionAnchorEl}
              onClose={handlePopClose}
            />
          </Box>
        )
      },
      disableColumnMenu: true,
      sortable: false,
      flex: 1,
      align: "right",
      headerAlign: "right"
    }
  ]

  return (
    <Stack sx={styles.containerWidth} gap={2} py={2}>
      <TextField
        fullWidth
        variant="outlined"
        value={searchText}
        onChange={handleChange}
        placeholder={translate("search_placeholder")}
        InputProps={{
          startAdornment: <SearchIcon sx={{ color: "#9B9B9B" }} />
        }}
        sx={{
          " input": {
            padding: "7px 13px 7px 13px!important",
            width: "200px"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: CHECKBOX_BORDER_COLOR,
              borderRadius: "0.5rem"
            }
          }
        }}
        label={undefined}
      />
      <GridTable
        sortable
        loading={isLoading}
        rows={rows}
        columns={columns}
        // rowCount={rows?.length}
        rowCount={totalUserCount}
        defaultPageModel={pagination}
        onPaginationModelChange={setPagination}
        disableColumnSelector={true}
      />
      <UpdateCustomerUser
        isOpen={isEditOpen}
        setClose={handleEditClose}
        selectedRow={clickedRow}
      />
      <CustomerVerifyEmail
        isOpen={isVerifyEmailOpen}
        setClose={handleVerifyEmailClose}
        selectedRow={clickedRow}
      />
      <ResendVerificationEmail
        isOpen={isResendVerificationOpen}
        setClose={handleResendVerifyEmailClose}
        selectedRow={clickedRow}
      />
      <ResetPassword
        isOpen={isResetPasswordOpen}
        setClose={handleResetPasswordClose}
        selectedRow={clickedRow}
      />
      <DeleteCustomerUser
        isOpen={isDeleteOpen}
        setClose={handleDeleteClose}
        selectedRow={clickedRow}
        setTotalUserCount={setTotalUserCount}
      />
    </Stack>
  )
}
